/**
 * Typed errors thrown by the lobby service. UI code can `instanceof`-check
 * these to render friendly messages.
 */

export class RoomNotFoundError extends Error {
  constructor( public code: string ) {
    super( `Room ${ code } not found` )
    this.name = 'RoomNotFoundError'
  }
}

export class RoomFullError extends Error {
  constructor( public code: string, public maxPlayers: number ) {
    super( `Room ${ code } is full (max ${ maxPlayers } players)` )
    this.name = 'RoomFullError'
  }
}

export class RoomLockedError extends Error {
  constructor( public code: string, public status: string ) {
    super( `Room ${ code } is ${ status }; new players cannot join` )
    this.name = 'RoomLockedError'
  }
}

export class RoomCodeCollisionError extends Error {
  constructor( public attempts: number ) {
    super( `Failed to generate a unique room code after ${ attempts } attempts` )
    this.name = 'RoomCodeCollisionError'
  }
}

/**
 * Thrown when an in-game mutation (e.g. apply move) is attempted but the
 * room's `state/main` doc doesn't exist. Indicates the room is still in lobby
 * or the doc was deleted.
 */
export class RoomNotInGameError extends Error {
  constructor( public code: string ) {
    super( `Room ${ code } has no game state document` )
    this.name = 'RoomNotInGameError'
  }
}

/**
 * Thrown when an in-game mutation is attempted but the public game state's
 * `status` is not `'active'` (i.e. the game has already ended).
 */
export class GameNotActiveError extends Error {
  constructor( public code: string, public status: string ) {
    super( `Game in room ${ code } is not active (status: ${ status })` )
    this.name = 'GameNotActiveError'
  }
}

/**
 * Thrown by `applyScotlandYardMove` when the engine rejects the supplied
 * `Move` as illegal. `engineCode` mirrors `EngineError.code` so callers can
 * branch on specific reasons (e.g. show "no taxi tickets left").
 */
export class IllegalMoveError extends Error {
  constructor( public engineCode: string, message: string ) {
    super( message )
    this.name = 'IllegalMoveError'
  }
}

/**
 * Thrown when a move requires Mr. X's private station but the
 * `rooms/{code}/private/mrx` doc is missing or malformed.
 */
export class MrxStateMissingError extends Error {
  constructor( public code: string ) {
    super( `Mr. X private state is missing for room ${ code }` )
    this.name = 'MrxStateMissingError'
  }
}
